/**
 * Position sizing utilities for personalized signal calls.
 *
 * MNQ (Micro Nasdaq) — $2 per index point, ~$1.5K margin
 * NQ  (E-mini Nasdaq) — $20 per index point, ~$15K margin
 *
 * For each signal we compute:
 *   risk$  = accountSize * (riskPercent / 100)
 *   stopDist (points) = |entry - stop|
 *   contracts = floor( risk$ / (stopDist * dollarsPerPoint) )
 *
 * If the resulting contract count is 0 (account too small for the stop
 * distance even at 1 contract), we surface that explicitly so the user
 * knows to skip or trade smaller risk.
 */

export const INSTRUMENT_SPECS = {
  MNQ: {
    symbol: 'MNQ',
    name: 'Micro E-mini Nasdaq',
    dollarsPerPoint: 2,
    minMarginEstimate: 1500,
  },
  NQ: {
    symbol: 'NQ',
    name: 'E-mini Nasdaq',
    dollarsPerPoint: 20,
    minMarginEstimate: 15000,
  },
};

export const ACCOUNT_SIZE_OPTIONS = [
  { value: 2500,   label: '$2.5K' },
  { value: 5000,   label: '$5K' },
  { value: 10000,  label: '$10K' },
  { value: 25000,  label: '$25K' },
  { value: 50000,  label: '$50K' },
  { value: 100000, label: '$100K+' },
];

export const RISK_OPTIONS = [
  { value: 0.5, label: '0.5%', sub: 'Conservative' },
  { value: 1,   label: '1%',   sub: 'Standard' },
  { value: 2,   label: '2%',   sub: 'Aggressive' },
];

export function recommendInstrument(accountSize) {
  if (!accountSize) return 'MNQ';
  if (accountSize >= 25000) return 'NQ';
  return 'MNQ';
}

export function calculateContracts({ accountSize, riskPercent, instrument, entry, stop, target }) {
  const spec = INSTRUMENT_SPECS[instrument] || INSTRUMENT_SPECS.MNQ;
  const e = Number(entry);
  const s = Number(stop);
  const t = Number(target);
  const stopDist = Math.abs(e - s);
  const targetDist = Math.abs(t - e);

  if (!accountSize || !riskPercent || !stopDist) {
    return null;
  }

  const riskBudget = accountSize * (riskPercent / 100);
  const lossPerContract = stopDist * spec.dollarsPerPoint;
  const winPerContract = targetDist * spec.dollarsPerPoint;
  const contracts = Math.max(0, Math.floor(riskBudget / lossPerContract));
  const rrRatio = stopDist > 0 ? targetDist / stopDist : 0;

  return {
    contracts,
    instrument: spec.symbol,
    spec,
    riskBudget,
    riskDollars: contracts * lossPerContract,
    rewardDollars: contracts * winPerContract,
    lossPerContract,
    winPerContract,
    rrRatio,
    tooSmall: contracts === 0,
  };
}

export function formatCurrency(n, opts = {}) {
  if (n == null || isNaN(n)) return '—';
  const abs = Math.abs(n);
  if (opts.short && abs >= 1000) {
    return `$${(n / 1000).toFixed(abs >= 10000 ? 0 : 1)}K`;
  }
  return n.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
}

const STORAGE_KEY = 'anq_trading_profile';

export function loadTradingProfile() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    return {
      accountSize: Number(parsed.accountSize) || null,
      riskPercent: Number(parsed.riskPercent) || 1,
      instrument: parsed.instrument || recommendInstrument(parsed.accountSize),
    };
  } catch {
    return null;
  }
}

export function saveTradingProfile(profile) {
  try {
    if (!profile) {
      localStorage.removeItem(STORAGE_KEY);
      return;
    }
    localStorage.setItem(STORAGE_KEY, JSON.stringify(profile));
  } catch {
    // ignore storage errors
  }
}
